import { createSlice } from "@reduxjs/toolkit"; // Importing createSlice from Redux Toolkit

const THEME_MODE_STORAGE_KEY = "themeMode";
const THEME_COLORS_STORAGE_KEY = "themeColors";
const isBrowser =
  typeof window !== "undefined" && typeof document !== "undefined";

const getStoredDarkMode = () => {
  if (!isBrowser) return false;
  return localStorage.getItem(THEME_MODE_STORAGE_KEY) === "dark";
};

const applyDarkModeClass = (darkMode: boolean) => {
  if (!isBrowser) return;

  if (darkMode) {
    document.body.classList.remove("light");
    document.body.classList.add("dark-only");
  } else {
    document.body.classList.remove("dark-only");
    document.body.classList.add("light");
  }
};

const applyStoredThemeColors = () => {
  if (!isBrowser) return;

  const themeColors = localStorage.getItem(THEME_COLORS_STORAGE_KEY);
  if (!themeColors) return;

  try {
    const parsedThemeColors = JSON.parse(themeColors);
    if (parsedThemeColors?.primary) {
      document.documentElement.style.setProperty(
        "--theme-default",
        parsedThemeColors.primary
      );
    }
    if (parsedThemeColors?.secondary) {
      document.documentElement.style.setProperty(
        "--theme-secondary",
        parsedThemeColors.secondary
      );
    }
  } catch {
    localStorage.removeItem(THEME_COLORS_STORAGE_KEY);
  }
};

const storedDarkMode = getStoredDarkMode();
applyDarkModeClass(storedDarkMode);
applyStoredThemeColors();

// Defining the initial state for the layout slice
const initialState = {
  flip: false, // State to toggle the flip action
  darkMode: storedDarkMode, // State to manage dark mode toggle
  toggleSidebar: false, // State to control sidebar visibility
  pinedMenu: [""], // State to manage the pinned menu items (array of strings)
  layoutName: "", // State to store the name of the layout
  responsiveSearch: false, // State to control responsive search toggle
  scroll: false, // State to track scroll behavior
  margin: 0, // State to manage the scroll margin
  hideHeader: false,
};

// Creating the layout slice with reducers and initial state
const LayoutSlice = createSlice({
  name: "LayoutSlice", // Name of the slice
  initialState, // Initial state defined above
  reducers: {
    // Reducer to toggle the flip state (boolean)
    setFlip: (state) => {
      state.flip = !state.flip; // Toggle the flip value
    },

    // Reducer to toggle dark mode and apply corresponding styles to the document body
    setDarkMode: (state, action) => {
      state.darkMode = action.payload; // Set darkMode based on the action payload
      applyDarkModeClass(state.darkMode);
      if (isBrowser) {
        localStorage.setItem(
          THEME_MODE_STORAGE_KEY,
          state.darkMode ? "dark" : "light"
        );
      }
    },

    // Reducer to toggle the sidebar visibility
    setToggleSidebar: (state, action) => {
      state.toggleSidebar = action.payload; // Set toggleSidebar based on the action payload
    },

    // Reducer to set the pinned menu items
    setPinedMenu: (state, action) => {
      state.pinedMenu = action.payload; // Set pinned menu based on the action payload
    },

    // Reducer to handle pin/unpin of a menu item
    handlePined: (state, action) => {
      if (action.payload) {
        if (state.pinedMenu.includes(action.payload)) {
          // If the item is already pinned, remove it from the pinned menu
          let filterMenu = state.pinedMenu.filter(
            (item) => item !== action.payload
          );
          state.pinedMenu = filterMenu; // Update the pinedMenu state
        } else {
          // If the item is not pinned, add it to the pinned menu
          state.pinedMenu = [...state.pinedMenu, action.payload];
        }
      }
    },

    // Reducer to handle responsive toggle of the sidebar
    handleResponsiveToggle: (state) => {
      state.toggleSidebar = true; // Set sidebar toggle to true for responsive design
    },

    // Reducer to toggle the responsive search state
    setResponsiveSearch: (state) => {
      state.responsiveSearch = !state.responsiveSearch; // Toggle the responsiveSearch value
    },

    // Reducer to set scroll behavior for the menu
    setScrollMenu: (state, action) => {
      state.scroll = action.payload; // Set scroll state based on the action payload
    },

    // Reducer to scroll the layout to the left by increasing the margin
    scrollToLeft: (state) => {
      state.margin += 500; // Increase margin to scroll to the left
    },

    // Reducer to scroll the layout to the right by decreasing the margin
    scrollToRight: (state) => {
      state.margin -= 500; // Decrease margin to scroll to the right
    },
    setHideHeader: (state, action) => {
      state.hideHeader = action.payload;
    },
  },
});

// Exporting actions to be used in components or other parts of the application
export const {
  setFlip,
  setDarkMode,
  setToggleSidebar,
  setPinedMenu,
  handlePined,
  handleResponsiveToggle,
  setResponsiveSearch,
  setScrollMenu,
  scrollToLeft,
  scrollToRight,
  setHideHeader,
} = LayoutSlice.actions;

// Exporting the reducer to be included in the Redux store
export default LayoutSlice.reducer;
